import { useEffect, useState } from "react";
import {
  lerBoletim,
  lerSituacao,
  textoDaSituacao,
  type LinhaDoBoletim,
  type SituacaoDoCurso,
} from "../lib/faculdade";
import {
  AcaoPrincipal,
  AvisoNavy,
  Nota,
  Selo,
  SeloDaSituacao,
  TituloDaSecao,
  data,
} from "./FaculdadeUi";
import Carregando from "./Carregando";

// Minhas notas — o boletim da pós, por TURMA.
//
// ---------------------------------------------------------------------------
// O que esta tela mostra que a do Bubble não mostrava
// ---------------------------------------------------------------------------
// 1. **As notas de disciplina que NÃO está na sua grade.** São 2.659 tentativas
//    reais (1,1%) de quem trocou de turma, ou de disciplina que saiu da grade
//    depois. Um boletim que só mostrasse a grade sumiria com elas sem dizer
//    nada — e perder nota é o lado sem volta do princípio 1. Elas vêm numa
//    seção própria, com o motivo escrito.
//
// 2. **Por que a lista está vazia, quando está.** São quatro motivos diferentes
//    (`sem_matricula`, `sem_turma`, `turma_desconhecida`, `grade_vazia`), e o
//    que o aluno faz em cada um é diferente. "A ausência de uma linha não
//    informa nada" — a cicatriz da aba Prazos da Admissão.
//
// 3. **Quantas tentativas restam.** O limite é 5 (medido na origem), e sem o
//    número na tela o aluno descobre que acabou ao clicar.
//
// 4. **Pendente / Aprovado / Reprovado em cada avaliação.** Pedido da reunião
//    de 31/08 com o time da secretaria, e a nota mínima que faltava veio na
//    mesma conversa: **6**. Até 30/08 esta tela não mostrava situação nenhuma,
//    porque a pergunta "existe nota mínima?" estava aberta no mapeamento desde
//    31/07 — e um "reprovado" errado na tela do aluno é pior que a ausência
//    dele. Quem decide é o BANCO (`pos_meu_boletim.situacao`), com a mesma
//    `pos_nota_minima()` que a trava 4 do certificado usa.
//
//    O dado confirma o número com um degrau de 36,7×: 186 pares
//    (aluno, disciplina) têm maior nota 5 e 6.834 têm 6 — formato de nota de
//    corte. E a aprovação é pela MAIOR nota das tentativas, o que o limite de 5
//    já implicava.
//
// ---------------------------------------------------------------------------
// O que ela NÃO mostra, de propósito
// ---------------------------------------------------------------------------
//
// **Média do curso.** Somar as notas aqui criaria a segunda definição de um
// número acadêmico — o mesmo defeito de agregar acesso em JavaScript. Se a
// instituição tiver uma fórmula, ela vem do banco.

export default function FaculdadeNotas({ cursoAtivo }: { cursoAtivo: string }) {
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [boletim, setBoletim] = useState<LinhaDoBoletim[]>([]);
  const [situacao, setSituacao] = useState<SituacaoDoCurso[]>([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [b, s] = await Promise.all([lerBoletim(), lerSituacao()]);
        if (!vivo) return;
        setBoletim(b);
        setSituacao(s);
        setFase("pronto");
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  if (fase === "carregando") return <Carregando />;
  if (fase === "erro") {
    return (
      <AvisoNavy titulo="Não conseguimos carregar as suas notas" tom="erro">
        Recarregue a página. Se continuar, fale com a secretaria — as suas notas continuam
        guardadas.
      </AvisoNavy>
    );
  }

  // A tela é de UM curso por vez: a Faculdade já tem o seletor de curso no topo,
  // e repetir a divisão aqui daria dois controles para a mesma escolha.
  const doCurso = boletim.filter((l) => l.curso_slug === cursoAtivo);
  const naGrade = doCurso.filter((l) => l.na_grade);
  const foraDaGrade = doCurso.filter((l) => !l.na_grade);
  const sit = situacao.find((s) => s.curso_slug === cursoAtivo);
  const aviso = sit ? textoDaSituacao(sit) : null;

  // APROVADAS, e não "com nota": é a contagem que decide o certificado (trava 4
  // de `sec_pode_solicitar`), então é a que o aluno precisa ver aqui. A de "com
  // nota" aparece ao lado só quando difere — quando toda prova feita passou, o
  // segundo número não diria nada e dois números iguais com rótulos diferentes
  // fazem procurar uma diferença que não existe.
  const aprovadas = naGrade.filter((l) => l.situacao === "aprovado").length;
  const feitas = naGrade.filter((l) => l.melhor_nota !== null).length;

  return (
    <>
      {/* O aviso vem ANTES da lista: quando ele existe, é ele que explica a
          lista curta ou vazia. Depois dela, seria a resposta chegando tarde. */}
      {aviso && (
        <div className="mb-6">
          <AvisoNavy titulo={aviso.titulo} tom={sit?.motivo === "ok" ? "espera" : "erro"}>
            {aviso.corpo}
          </AvisoNavy>
        </div>
      )}

      {naGrade.length > 0 && (
        <>
          <TituloDaSecao
            nota={
              // CONTAGEM, e não porcentagem. É a mesma decisão do Gerenciador de
              // Estudos: o denominador da grade pode mudar (a turma ganha uma
              // disciplina), e uma barra que anda para trás sem o aluno fazer
              // nada destrói a confiança no número.
              `${aprovadas} de ${naGrade.length} aprovadas` +
              (feitas > aprovadas ? ` · ${feitas} com nota` : "") +
              (sit?.turma_numero ? ` · Turma ${sit.turma_numero}` : "")
            }
          >
            Disciplinas da sua turma
          </TituloDaSecao>
          <div className="flex flex-col gap-3">
            {naGrade.map((l, i) => (
              <LinhaDeDisciplina key={l.disciplina_id} linha={l} i={i} />
            ))}
          </div>
        </>
      )}

      {foraDaGrade.length > 0 && (
        <div className="mt-9">
          <TituloDaSecao nota={`${foraDaGrade.length} disciplina(s)`}>
            Outras notas suas neste curso
          </TituloDaSecao>
          {/* Esta explicação não é enfeite: sem ela o aluno vê uma disciplina que
              não reconhece na grade dele e conclui que a tela está errada. */}
          <p className="mb-4 max-w-2xl text-sm text-navy-mute">
            Você tem nota nestas disciplinas, e elas não estão na grade da sua turma — costuma ser
            de quem trocou de turma, ou de disciplina que saiu da grade depois. As notas continuam
            valendo e ficam guardadas aqui.
          </p>
          <div className="flex flex-col gap-3">
            {foraDaGrade.map((l, i) => (
              <LinhaDeDisciplina key={l.disciplina_id} linha={l} i={i} foraDaGrade />
            ))}
          </div>
        </div>
      )}

      {doCurso.length === 0 && !aviso && (
        <AvisoNavy titulo="Nenhuma disciplina por aqui ainda">
          Assim que a grade da sua turma for publicada, as disciplinas e as notas aparecem nesta
          aba.
        </AvisoNavy>
      )}
    </>
  );
}

function LinhaDeDisciplina({
  linha,
  i,
  foraDaGrade = false,
}: {
  linha: LinhaDoBoletim;
  i: number;
  foraDaGrade?: boolean;
}) {
  const temNota = linha.melhor_nota !== null;
  const podeFazer =
    !foraDaGrade && linha.total_questoes >= 10 && (linha.tentativas_restantes > 0 || linha.em_andamento);

  return (
    // `data-disciplina` e `data-nota` são ESCOPO para as provas de tela.
    //
    // Custam nada e evitam a cicatriz dos sete rótulos iguais: numa tela real o
    // mesmo texto aparece mais de uma vez, e seletor por texto sem escopo pega o
    // elemento errado — em `calculadora-tela.mjs` ele escreveu num checkbox e
    // devolveu "ok". Afrouxar a asserção transferiria o problema para a próxima
    // pessoa.
    <div
      data-disciplina={linha.disciplina_id}
      className="tv-card rise flex flex-wrap items-center gap-x-6 gap-y-4 p-5"
      style={{ "--i": Math.min(i + 1, 8) } as React.CSSProperties}
    >
      <div className="min-w-56 flex-1">
        <h3 className="font-display font-semibold text-white">{linha.disciplina}</h3>
        <p className="mt-0.5 text-sm text-navy-mute">
          {[
            linha.docente_nome
              ? linha.docente_nome + (linha.docente_titulacao ? ` · ${linha.docente_titulacao}` : "")
              : null,
            linha.carga_horaria_horas ? `${linha.carga_horaria_horas}h` : null,
          ]
            .filter(Boolean)
            .join(" — ") || "Sem docente informado"}
        </p>
        {/* A última tentativa só aparece quando difere da melhor. Repetir o mesmo
            número duas vezes com rótulos diferentes faz o aluno procurar uma
            diferença que não existe. */}
        {temNota && (
          <p className="mt-1.5 text-xs text-navy-mute">
            {linha.tentativas} tentativa{linha.tentativas === 1 ? "" : "s"}
            {linha.ultima_em ? ` · última em ${data(linha.ultima_em)}` : ""}
            {linha.ultima_nota !== null && linha.ultima_nota !== linha.melhor_nota
              ? ` · última nota ${linha.ultima_nota}`
              : ""}
          </p>
        )}
      </div>

      <div data-nota className="flex items-center gap-3">
        <Nota valor={linha.melhor_nota} />
        {temNota && linha.tentativas > 1 && (
          <span className="text-xs text-navy-mute">melhor</span>
        )}
        {/* A situação vem DEPOIS do número, e não antes: o aluno procura a nota,
            e o selo é a leitura dela. Antes, o selo competiria com o número que
            ele veio ver. */}
        <SeloDaSituacao situacao={linha.situacao} />
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {linha.em_andamento ? (
          <AcaoPrincipal para={`/faculdade/prova/${linha.disciplina_id}`}>Continuar</AcaoPrincipal>
        ) : podeFazer ? (
          <AcaoPrincipal para={`/faculdade/prova/${linha.disciplina_id}`}>
            {temNota ? "Fazer de novo" : "Fazer a prova"}
          </AcaoPrincipal>
        ) : foraDaGrade ? (
          <Selo tom="espera">Fora da sua grade</Selo>
        ) : linha.total_questoes < 10 ? (
          // Disciplina sem prova cadastrada. Medido: 19 das 217 não têm QUESTÃO
          // nenhuma. Dizer "prova indisponível" é melhor que um botão que
          // estoura, e melhor que a ausência do botão sem explicação.
          <Selo tom="espera">Prova ainda não disponível</Selo>
        ) : (
          <Selo tom="espera">Sem tentativas</Selo>
        )}
      </div>

      {/* O número de tentativas restantes fica embaixo, na largura toda: é a
          informação que o aluno procura DEPOIS de decidir refazer, e no meio da
          linha ele competiria com a nota. */}
      {!foraDaGrade && linha.total_questoes >= 10 && (
        <p className="w-full text-xs text-navy-mute">
          {linha.tentativas_restantes > 0
            ? `${linha.tentativas_restantes} tentativa${linha.tentativas_restantes === 1 ? "" : "s"} restante${linha.tentativas_restantes === 1 ? "" : "s"}`
            : "Você usou todas as tentativas desta disciplina. A secretaria pode liberar mais."}
        </p>
      )}
    </div>
  );
}
